// apiService.js
import apiClient from './apiClient';
import { useState } from 'react';
import { normalizeSchoolsApiResponse } from './schoolDataNormalizer';

// Debug tracking
const debugApiCalls = {};

const trackApiCall = (name) => {
  if (!debugApiCalls[name]) {
    debugApiCalls[name] = [];
  }
  const stack = new Error().stack;
  debugApiCalls[name].push({
    timestamp: Date.now(),
    stack: stack
  });
};

export const handleApiError = (error, enqueueSnackbar) => {
  if (!enqueueSnackbar) return;  // Early return if no snackbar provided
  
  if (error.isNetwork) {
    enqueueSnackbar('Unable to connect to the server. Please check your connection.', { 
      variant: 'error' 
    });
  } else if (error.isServer) {
    enqueueSnackbar('An unexpected error occurred. Please try again later.', { 
      variant: 'error' 
    });
  } else {
    enqueueSnackbar(error.message || 'An error occurred', { variant: 'error' });
  }
};

// Result cache with proper memory management
const resultCache = new Map();
const resultTimeouts = new Map();
const CACHE_DURATION = 3600000; // 60 minutes

const clearResultCache = (key) => {
  if (resultTimeouts.has(key)) {
    clearTimeout(resultTimeouts.get(key));
  }
  resultCache.delete(key);
  resultTimeouts.delete(key);
};

const withCache = async (key, fetchFn) => {
  const now = Date.now();
  const cached = resultCache.get(key);
  
  // Add request deduplication
  if (cached?.promise) {
    return cached.promise;
  }
  
  if (cached?.data && now - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }

  // Create a promise for the request
  const promise = fetchFn();
  
  // Store the promise in cache temporarily
  resultCache.set(key, {
    promise,
    timestamp: now
  });

  try {
    const data = await promise;
    // Store the actual data
    resultCache.set(key, {
      data,
      timestamp: now
    });
    return data;
  } catch (error) {
    // Clear cache on error
    resultCache.delete(key);
    throw error;
  }
};

// Improved cache invalidation
const invalidateCache = (pattern) => {
  for (const key of resultCache.keys()) {
    if (pattern instanceof RegExp ? pattern.test(key) : key.includes(pattern)) {
      clearResultCache(key);
    }
  }
};

// Export cache invalidation for external use
export const clearApiCache = (pattern) => {
  invalidateCache(pattern);
};

export const schoolsApi = {
  // NEW V2 API METHODS (using edc_unified database)
  async getAllSchools(enqueueSnackbar, options = {}) {
    const cacheKey = options.limit ? `getAllSchools-v2-limit-${options.limit}` : 'getAllSchools-v2';
    return withCache(cacheKey, async () => {
      trackApiCall('getAllSchools-v2');
      try {
        // If limit is provided, use the paginated API with appropriate page size
        if (options.limit) {
          const response = await apiClient.get('/v2/schools', {
            params: {
              paginated: 'true',
              page: 1,
              page_size: options.limit
            }
          });
          const normalizedResponse = normalizeSchoolsApiResponse(response);
          return normalizedResponse.schools || [];
        } else {
          // Regular call without pagination
          const response = await apiClient.get('/v2/schools');
          const normalized = normalizeSchoolsApiResponse(response);
          return normalized;
        }
      } catch (error) {
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // Add a specific method for paginated schools (V2)
  async getSchoolsPaginated(page = 1, pageSize = 100, enqueueSnackbar) {
    return withCache(`schools-v2-page-${page}-size-${pageSize}`, async () => {
      trackApiCall('getSchoolsPaginated-v2');
      try {
        const response = await apiClient.get('/v2/schools', {
          params: {
            paginated: 'true',
            page,
            page_size: pageSize
          }
        });
        return normalizeSchoolsApiResponse(response);
      } catch (error) {
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // Get school by UUID or NCES number (V2)
  async getSchoolById(identifier, enqueueSnackbar) {
    return withCache(`school-v2-${identifier}`, async () => {
      trackApiCall('getSchoolById-v2');
      try {
        const response = await apiClient.get(`/v2/school/${identifier}`);
        return normalizeSchoolsApiResponse([response])[0]; // Normalize single school
      } catch (error) {
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // NEW TEAMS-BASED API METHOD (uses organizational structure tables directly)
  async getSchoolsByTeams(teamIds = [], teamGroupIds = [], options = {}, enqueueSnackbar) {
    const cacheKey = `schools-teams-${teamIds.sort().join(',')}-${teamGroupIds.sort().join(',')}-${JSON.stringify(options)}`;
    return withCache(cacheKey, async () => {
      trackApiCall('getSchoolsByTeams-v2');
      try {
        const requestData = {
          team_ids: teamIds,
          team_group_ids: teamGroupIds,
          paginated: options.paginated || false,
          page: options.page || 1,
          page_size: options.page_size || 300
        };

        const response = await apiClient.post('/v2/user/teams/schools', requestData);
        
        // The response is already normalized from the backend
        return response;
      } catch (error) {
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // Get user's teams with metadata (enriched with team names from database)
  async getUserTeams(enqueueSnackbar) {
    return withCache('user-teams-v2', async () => {
      trackApiCall('getUserTeams-v2');
      try {
        const response = await apiClient.get('/v2/user/teams');
        return response;
      } catch (error) {
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },



  async getMarketShareData(schoolId, enqueueSnackbar = null, travelMode = 'drive') {
    return withCache(`marketShare-${schoolId}-${travelMode}`, async () => {
      try {
        return await apiClient.get(`/v2/school/${schoolId}/esri-market-share?travel_mode=${travelMode}`);
      } catch (error) {
        if (enqueueSnackbar) {
          handleApiError(error, enqueueSnackbar);
        }
        throw error;
      }
    });
  },

  // ========== V2 API METHODS (UNIFIED DATABASE) ==========
  
  // V2: Get enrollment projections with multi-year support and force regeneration
  getEnrollmentProjectionsV2: async (schoolId, forceRegenerate = false, enqueueSnackbar) => {
    const cacheKey = `projections-v2-${schoolId}`;
    
    // Skip cache if forcing regeneration
    if (forceRegenerate) {
      invalidateCache(cacheKey);
    }
    
    return withCache(cacheKey, async () => {
      try {
        const url = `/v2/school/${schoolId}/enrollment-projections${forceRegenerate ? '?force_regenerate=true' : ''}`;
        return await apiClient.get(url);
      } catch (error) {
        let errorMessage;
        if (error.status === 401 || error.status === 403) {
          errorMessage = 'You do not have permission to view this school\'s projections';
        } else if (error.status === 404) {
          errorMessage = 'School projections not found';
        } else if (typeof error.response === 'string' && error.response.includes('<!DOCTYPE')) {
          errorMessage = 'Authentication error. Please try logging in again.';
        } else {
          errorMessage = error.message || 'Failed to fetch enrollment projections';
        }

        error.message = errorMessage;
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // V2: Get enrollment data for a school (supports multi-year)
  getEnrollmentDataV2: async (schoolId, enqueueSnackbar) => {
    return withCache(`enrollment-data-v2-${schoolId}`, async () => {
      try {
        return await apiClient.get(`/v2/school/${schoolId}/enrollment-data`);
      } catch (error) {
        let errorMessage;
        if (error.status === 401 || error.status === 403) {
          errorMessage = 'You do not have permission to view this school\'s enrollment data';
        } else if (error.status === 404) {
          errorMessage = 'School enrollment data not found';
        } else {
          errorMessage = error.message || 'Failed to fetch enrollment data';
        }

        error.message = errorMessage;
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // V2: Submit enrollment data with multi-year support
  submitEnrollmentDataV2: async (schoolId, enrollmentData, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/v2/school/${schoolId}/enrollment-data`, {
        enrollmentData
      });
      
      // Invalidate relevant caches
      invalidateCache(/projections-v2-/);
      invalidateCache(/enrollment-data-v2-/);
      
      return response;
    } catch (error) {
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to submit enrollment data for this school';
      } else if (error.status === 400) {
        errorMessage = 'Invalid enrollment data format';
      } else {
        errorMessage = error.message || 'Failed to submit enrollment data';
      }

      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  // V2: Generate updated projections
  generateProjectionsV2: async (schoolId, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/v2/school/${schoolId}/projections/generate`);
      invalidateCache(`projections-v2-${schoolId}`);
      return response;
    } catch (error) {
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to generate projections for this school';
      } else if (error.status === 404) {
        errorMessage = 'No user enrollment data found for projections';
      } else {
        errorMessage = error.message || 'Failed to generate projections';
      }

      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },



  // ========== ENROLLMENT MANAGEMENT API METHODS ==========

  // Get comprehensive enrollment management data
  getEnrollmentManagementData: async (schoolUuid, totalsOnly = false, enqueueSnackbar) => {
    const cacheKey = `enrollment-management-${schoolUuid}${totalsOnly ? '-totals' : ''}`;
    return withCache(cacheKey, async () => {
      try {
        const params = totalsOnly ? { totals_only: 'true' } : {};
        return await apiClient.get(`/v2/school/${schoolUuid}/enrollment-management`, { params });
      } catch (error) {
        let errorMessage;
        if (error.status === 401 || error.status === 403) {
          errorMessage = 'You do not have permission to view enrollment data for this school';
        } else if (error.status === 404) {
          errorMessage = 'School enrollment data not found';
        } else {
          errorMessage = error.message || 'Failed to fetch enrollment management data';
        }

        error.message = errorMessage;
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // Save enrollment data with validation
  saveEnrollmentData: async (schoolUuid, enrollmentData, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/v2/school/${schoolUuid}/enrollment-data`, enrollmentData);
      
      // Invalidate related caches
      invalidateCache(`enrollment-management-${schoolUuid}`);
      invalidateCache(`enrollment-management-${schoolUuid}-totals`);
      invalidateCache(`enrollment-projections-${schoolUuid}`);
      invalidateCache(`projections-v2-${schoolUuid}`);
      
      return response;
    } catch (error) {
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to save enrollment data for this school';
      } else if (error.status === 400) {
        errorMessage = error.response?.data?.message || 'Invalid enrollment data provided';
      } else {
        errorMessage = error.message || 'Failed to save enrollment data';
      }

      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  // Delete enrollment data
  deleteEnrollmentData: async (schoolUuid, deletionData, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/v2/school/${schoolUuid}/enrollment-data`, {
        data: deletionData
      });
      
      // Invalidate related caches
      invalidateCache(`enrollment-management-${schoolUuid}`);
      invalidateCache(`enrollment-management-${schoolUuid}-totals`);
      invalidateCache(`enrollment-projections-${schoolUuid}`);
      invalidateCache(`projections-v2-${schoolUuid}`);
      
      return response;
    } catch (error) {
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to delete enrollment data for this school';
      } else if (error.status === 400) {
        errorMessage = error.response?.data?.message || 'Invalid deletion request';
      } else {
        errorMessage = error.message || 'Failed to delete enrollment data';
      }

      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  // Update existing enrollment data
  updateEnrollmentData: async (schoolUuid, enrollmentData, enqueueSnackbar) => {
    try {
      const response = await apiClient.put(`/v2/school/${schoolUuid}/enrollment-data`, enrollmentData);
      
      // Invalidate related caches
      invalidateCache(`enrollment-management-${schoolUuid}`);
      invalidateCache(`projections-v2-${schoolUuid}`);
      
      return response;
    } catch (error) {
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to update enrollment data for this school';
      } else if (error.status === 400) {
        errorMessage = error.response?.data?.message || 'Invalid enrollment data provided';
      } else {
        errorMessage = error.message || 'Failed to update enrollment data';
      }

      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  // Validate enrollment data without saving
  validateEnrollmentData: async (schoolUuid, enrollmentData, enqueueSnackbar) => {
    try {
      return await apiClient.post(`/v2/school/${schoolUuid}/enrollment-validation`, enrollmentData);
    } catch (error) {
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to validate enrollment data for this school';
      } else {
        errorMessage = error.message || 'Failed to validate enrollment data';
      }

      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  // V2: Get enrollment targets with multi-year support
  getEnrollmentTargetsV2: async (schoolId, schoolYears = null, enqueueSnackbar) => {
    const cacheKey = schoolYears 
      ? `targets-v2-${schoolId}-${schoolYears.join(',')}`
      : `targets-v2-${schoolId}`;
      
    return withCache(cacheKey, async () => {
      try {
        const params = schoolYears ? { school_years: schoolYears.join(',') } : {};
        return await apiClient.get(`/v2/school/${schoolId}/enrollment-targets`, { params });
      } catch (error) {
        let errorMessage;
        if (error.status === 401 || error.status === 403) {
          errorMessage = 'You do not have permission to view this school\'s enrollment targets';
        } else if (error.status === 404) {
          errorMessage = 'School targets not found';
        } else {
          errorMessage = error.message || 'Failed to fetch enrollment targets';
        }

        error.message = errorMessage;
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // V2: Submit enrollment targets with multi-year support
  submitEnrollmentTargetsV2: async (schoolId, targets, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/v2/school/${schoolId}/enrollment-targets`, { targets });
      invalidateCache(/targets-v2-/);
      return response;
    } catch (error) {
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to submit enrollment targets for this school';
      } else if (error.status === 400) {
        errorMessage = 'Invalid targets data format';
      } else {
        errorMessage = error.message || 'Failed to submit enrollment targets';
      }

      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  // V2: Delete enrollment targets for a school
  deleteEnrollmentTargetsV2: async (schoolId, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/v2/school/${schoolId}/enrollment-targets`);
      invalidateCache(/targets-v2-/);
      return response;
    } catch (error) {
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to delete enrollment targets for this school';
      } else if (error.status === 404) {
        errorMessage = 'No enrollment targets found to delete';
      } else {
        errorMessage = error.message || 'Failed to delete enrollment targets';
      }

      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  // ========== GRADE CAPACITY API METHODS ==========

  // Get grade capacities
  getGradeCapacities: async (schoolId, enqueueSnackbar) => {
    const cacheKey = `capacities-${schoolId}`;
    return withCache(cacheKey, async () => {
      try {
        return await apiClient.get(`/v2/school/${schoolId}/grade-capacities`);
      } catch (error) {
        let errorMessage;
        if (error.status === 401 || error.status === 403) {
          errorMessage = 'You do not have permission to view grade capacities for this school';
        } else if (error.status === 404) {
          errorMessage = 'School grade capacities not found';
        } else {
          errorMessage = error.message || 'Failed to fetch grade capacities';
        }

        error.message = errorMessage;
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // Save grade capacities
  saveGradeCapacities: async (schoolId, capacities, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/v2/school/${schoolId}/grade-capacities`, { capacities });
      
      // Invalidate related caches
      invalidateCache(`capacities-${schoolId}`);
      invalidateCache(`projections-v2-${schoolId}`);
      
      return response;
    } catch (error) {
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to save grade capacities for this school';
      } else if (error.status === 400) {
        errorMessage = 'Invalid grade capacity data format';
      } else {
        errorMessage = error.message || 'Failed to save grade capacities';
      }

      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  // Delete grade capacities
  deleteGradeCapacities: async (schoolId, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/v2/school/${schoolId}/grade-capacities`);
      
      // Invalidate related caches
      invalidateCache(`capacities-${schoolId}`);
      invalidateCache(`projections-v2-${schoolId}`);
      
      return response;
    } catch (error) {
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to delete grade capacities for this school';
      } else if (error.status === 404) {
        errorMessage = 'No grade capacities found to delete';
      } else {
        errorMessage = error.message || 'Failed to delete grade capacities';
      }

      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  // ========== LEGACY API METHODS ==========

  getEnrollmentProjections: async (schoolId, enqueueSnackbar) => {
    return withCache(`projections-${schoolId}`, async () => {
      try {
        return await apiClient.get(`/school/${schoolId}/enrollment-projections`);
      } catch (error) {

        let errorMessage;
        if (error.status === 401 || error.status === 403) {
          errorMessage = 'You do not have permission to view this school\'s projections';
        } else if (error.status === 404) {
          errorMessage = 'School projections not found';
        } else if (typeof error.response === 'string' && error.response.includes('<!DOCTYPE')) {
          errorMessage = 'Authentication error. Please try logging in again.';
        } else {
          errorMessage = error.message || 'Failed to fetch enrollment projections';
        }

        error.message = errorMessage;
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  submitEnrollmentData: async (data, enqueueSnackbar) => {
    try {
      const response = await apiClient.post('/submit-enrollment-data', data);
      invalidateCache(/projections-/);
      return response;
    } catch (error) {
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  updateProjections: async (ncessch, enqueueSnackbar) => {
    try {
      const response = await apiClient.post('/update-projections', { ncessch });
      invalidateCache(`projections-${ncessch}`);
      return response;
    } catch (error) {
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },
  
  submitEnrollmentTargets: async (data, enqueueSnackbar) => {
    try {
      const response = await apiClient.post('/submit-enrollment-targets', data);
      invalidateCache(`targets-${data.schoolId}`);
      return response;
    } catch (error) {
  
      let errorMessage;
      if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to submit enrollment targets for this school';
      } else if (error.status === 404) {
        errorMessage = 'School not found';
      } else if (typeof error.response === 'string' && error.response.includes('<!DOCTYPE')) {
        errorMessage = 'Authentication error. Please try logging in again.';
      } else {
        errorMessage = error.message || 'Failed to submit enrollment targets';
      }
  
      error.message = errorMessage;
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },
  
  getEnrollmentTargets: async (schoolId, enqueueSnackbar) => {
    return withCache(`targets-${schoolId}`, async () => {
      try {
        return await apiClient.get(`/school/${schoolId}/enrollment-targets`);
      } catch (error) {
  
        let errorMessage;
        if (error.status === 401 || error.status === 403) {
          errorMessage = 'You do not have permission to view this school\'s enrollment targets';
        } else if (error.status === 404) {
          errorMessage = 'School targets not found';
        } else if (typeof error.response === 'string' && error.response.includes('<!DOCTYPE')) {
          errorMessage = 'Authentication error. Please try logging in again.';
        } else {
          errorMessage = error.message || 'Failed to fetch enrollment targets';
        }
  
        error.message = errorMessage;
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },
  
  async getNearbySchools(schoolId, driveTime, type = 'current', enqueueSnackbar = null, travelMode = 'drive') {
    return withCache(`nearby-${schoolId}-${driveTime}-${type}-${travelMode}`, async () => {
      try {
        return await apiClient.get(`/v2/school/${schoolId}/nearby-schools/${driveTime}?type=${type}&travel_mode=${travelMode}`);
      } catch (error) {
        if (enqueueSnackbar) {
          handleApiError(error, enqueueSnackbar);
        }
        return [];  // Keeping existing behavior of returning empty array
      }
    });
  },
  
  // Single bulk request rather than one per drive time and relationship type. The endpoint
  // derives the available drive times itself, so callers no longer need ESRI data first.
  async getAllNearbySchools(schoolId, enqueueSnackbar = null, travelMode = 'drive') {
    return withCache(`allNearby-${schoolId}-${travelMode}`, async () => {
      try {
        const response = await apiClient.get(
          `/v2/school/${schoolId}/nearby-schools?travel_mode=${travelMode}`
        );

        return {
          current: response?.current || {},
          comparison: response?.comparison || {},
          gradeData: response?.gradeData || {},
          baseline: response?.baseline || null
        };
      } catch (error) {
        console.error('❌ Error in getAllNearbySchools:', error);
        if (enqueueSnackbar) {
          handleApiError(error, enqueueSnackbar);
        }
        throw error;
      }
    });
  },
  
  async getDistrictBoundaries(schoolId, enqueueSnackbar = null) {
    return withCache(`districtBoundaries-${schoolId}`, async () => {
      try {
        const response = await apiClient.get(`/v2/school/${schoolId}/district-boundaries`);
        return response;
      } catch (error) {
        console.error('❌ Error fetching district boundaries:', error);
        if (enqueueSnackbar) {
          handleApiError(error, enqueueSnackbar);
        }
        throw error;
      }
    });
  },
  
  async getSchoolsEnrollmentData(schoolIds, selectedSchoolId, enqueueSnackbar) {
    if (!schoolIds?.length) return [];
  
    const cacheKey = `enrollment-${selectedSchoolId}-${schoolIds.join(',')}`;
    return withCache(cacheKey, async () => {
      try {
        const CHUNK_SIZE = 100;
        const results = [];
        
        for (let i = 0; i < schoolIds.length; i += CHUNK_SIZE) {
          const chunk = schoolIds.slice(i, i + CHUNK_SIZE);
          
          const response = await apiClient.post('/v2/schools-enrollment-by-grade', { 
            uuidList: chunk,
            selectedSchool: selectedSchoolId
          });
          
          if (response && Array.isArray(response)) {
            results.push(...response);
          } else {
            console.warn('[Enrollment] response is not an array:', typeof response, '| first 100 chars:', String(response).slice(0, 100));
          }
        }
        
        return results;
      } catch (error) {
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },
  
  // District metrics using new v2 API with UUID-based access control
  async getDistrictMetrics(enqueueSnackbar) {
    return withCache('districtMetrics-v2', async () => {
      trackApiCall('getDistrictMetrics-v2');
      try {
        const response = await apiClient.get('/v2/district-metrics');
        
        if (!response?.metrics) {
          throw new Error('Invalid district metrics response');
        }

        return response;
      } catch (error) {
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // Legacy district metrics endpoint (deprecated - use getDistrictMetrics instead)
  async getDistrictMetricsLegacy(enqueueSnackbar) {
    return withCache('districtMetrics-legacy', async () => {
      try {
        const response = await apiClient.get('/district-metrics');
        
        if (!response?.metrics) {
          throw new Error('Invalid district metrics response');
        }

        return response;
      } catch (error) {
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  },

  // Get enrollment data for projections (totals only, optimized)
  getEnrollmentDataForProjections: async (schoolUuid, enqueueSnackbar) => {
    return withCache(`enrollment-projections-${schoolUuid}`, async () => {
      try {
        // Use totals_only=true to get optimized data for projections
        return await apiClient.get(`/v2/school/${schoolUuid}/enrollment-management`, {
          params: { totals_only: 'true' }
        });
      } catch (error) {
        let errorMessage;
        if (error.status === 401 || error.status === 403) {
          errorMessage = 'You do not have permission to view enrollment data for this school';
        } else if (error.status === 404) {
          errorMessage = 'School enrollment data not found';
        } else {
          errorMessage = error.message || 'Failed to fetch enrollment data for projections';
        }

        error.message = errorMessage;
        handleApiError(error, enqueueSnackbar);
        throw error;
      }
    });
  }
};

export function useApiCall(apiCall, onSuccess, onError, showLoading = true) {
  const [loading, setLoading] = useState(showLoading);
  const [error, setError] = useState(null);

  const execute = async (...args) => {
    try {
      setLoading(true);
      setError(null);

      const result = await apiCall(...args);
      
      if (!result || (typeof result === 'string' && result.includes('<!DOCTYPE'))) {
        throw new Error('Invalid response received');
      }

      if (onSuccess) onSuccess(result);
      return result;
    } catch (err) {

      let errorMessage = err.message;
      
      if (err.status === 401 || 
          err.message.includes('unauthorized') || 
          err.message.includes('<!DOCTYPE')) {
        errorMessage = 'Your session has expired. Please log in again.';
      }

      setError(errorMessage);
      if (onError) onError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { execute, loading, error };
}